import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';

/* ── Theme ─────────────────────────────────────────────────── */
const GOLD       = '#C9952A';
const GOLD_LIGHT = '#E8B84B';
const CREAM      = '#FAF6ED';
const WARM_WHITE = '#FFFCF5';
const TEXT       = '#2A1F0E';
const TEXT_MID   = '#6B5540';

const float = keyframes`
  0%   { transform: translateY(0px); }
  50%  { transform: translateY(-8px); }
  100% { transform: translateY(0px); }
`;

const shimmer = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const PageContainer = styled.div`
  background: ${CREAM};
  overflow: hidden;
`;

const Hero = styled.section`
  position: relative;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5rem 2rem 8rem;
  background: linear-gradient(135deg, #0C1410 0%, #122018 55%, #0C1410 100%);
  overflow: hidden;
  clip-path: polygon(0 0, 100% 0, 100% 88%, 0 100%);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse at 50% -10%, rgba(201,149,42,0.28) 0%, transparent 55%),
      radial-gradient(ellipse at 5%  90%, rgba(201,149,42,0.10) 0%, transparent 40%),
      radial-gradient(ellipse at 95% 90%, rgba(201,149,42,0.10) 0%, transparent 40%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cellipse cx='40' cy='12' rx='8' ry='18' fill='rgba(201%2C149%2C42%2C0.04)' transform='rotate(0%2C40%2C40)'/%3E%3Cellipse cx='40' cy='12' rx='8' ry='18' fill='rgba(201%2C149%2C42%2C0.04)' transform='rotate(90%2C40%2C40)'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 0;
  }
`;

const HeroContent = styled.div`
  max-width: 860px;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const HeroTitle = styled.h1`
  font-family: 'Playfair Display', serif;
  font-size: clamp(2.8rem, 5.5vw, 4.5rem);
  font-weight: 900;
  margin-bottom: 0.8rem;
  line-height: 1.05;
  background: linear-gradient(135deg, #fff 0%, ${GOLD_LIGHT} 50%, #fff 100%);
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${shimmer} 6s ease infinite, ${float} 7s ease-in-out infinite;
`;

const HeroSub = styled.p`
  font-size: 1.1rem;
  color: rgba(255,255,255,0.65);
  font-family: 'Source Serif 4', serif;
`;

const GoldRule = styled.div`
  width: 60px;
  height: 1.5px;
  background: linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT});
  margin: 1.4rem auto;
  border-radius: 2px;
`;

const MainContent = styled.div`
  max-width: 800px;
  margin: -4rem auto 0;
  padding: 0 2rem 5rem;
  position: relative;
  z-index: 2;
`;

const Section = styled.section`
  background: ${WARM_WHITE};
  border-radius: 20px;
  padding: 2.8rem;
  margin-bottom: 2rem;
  box-shadow: 0 8px 28px rgba(201,149,42,0.09);
  border: 1px solid rgba(201,149,42,0.12);
`;

const SectionTitle = styled.h2`
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.6rem, 2.5vw, 2.1rem);
  color: ${TEXT};
  margin-bottom: 1.8rem;
  text-align: center;
  position: relative;

  &::after {
    content: '';
    display: block;
    width: 52px;
    height: 2px;
    background: linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT});
    border-radius: 2px;
    margin: 0.75rem auto 0;
  }
`;

const ContactInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 2rem;
  margin: 1.5rem 0;
`;

const InfoCard = styled.div`
  text-align: center;
  padding: 2rem 1.5rem;
  background: ${CREAM};
  border-radius: 16px;
  border-top: 3px solid ${GOLD};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 30px rgba(201,149,42,0.12);
  }

  .icon {
    font-size: 2rem;
    margin-bottom: 0.8rem;
  }

  h3 {
    font-family: 'Playfair Display', serif;
    font-size: 1.15rem;
    color: ${TEXT};
    margin-bottom: 0.6rem;
  }

  p {
    color: ${TEXT_MID};
    line-height: 1.7;
    font-size: 0.95rem;
    font-family: 'Source Serif 4', serif;
  }

  a {
    color: ${GOLD};
    text-decoration: none;
    font-weight: 600;

    &:hover { text-decoration: underline; color: #9A6F1A; }
  }
`;

const SiteFooter = styled.footer`
  text-align: center;
  padding: 2rem 1rem 2.5rem;
  font-size: 0.84rem;
  color: ${TEXT_MID};
  font-family: 'Source Serif 4', serif;

  a {
    color: ${GOLD};
    text-decoration: none;
    font-weight: 600;
    &:hover { color: #9A6F1A; text-decoration: underline; }
  }

  span { margin: 0 0.5rem; opacity: 0.4; }
`;

const Contact = () => (
  <PageContainer>
    <Hero>
      <HeroContent>
        <HeroTitle>Contact Us</HeroTitle>
        <GoldRule />
        <HeroSub>Get in touch for queries or collaborations</HeroSub>
      </HeroContent>
    </Hero>

    <MainContent>
      <Section>
        <SectionTitle>Contact Information</SectionTitle>
        <ContactInfo>
          <InfoCard>
            <div className="icon">✉️</div>
            <h3>Email</h3>
            <p><a href="mailto:namaste@bheri.com">namaste@bheri.com</a></p>
          </InfoCard>
          <InfoCard>
            <div className="icon">📍</div>
            <h3>Address</h3>
            <p>
              Indian Institute of Technology Hyderabad<br />
              Kandi, Sangareddy<br />
              Telangana — 502285
            </p>
          </InfoCard>
        </ContactInfo>
      </Section>
    </MainContent>

    <SiteFooter>
      © {new Date().getFullYear()} BHERI Technology Foundation
      <span>·</span>
      <Link to="/privacy">Privacy Policy</Link>
    </SiteFooter>
  </PageContainer>
);

export default Contact;
